/**
 * Resolved paint for the map renderer — the bridge between a design system
 * expressed as CSS custom properties and a renderer that must also run where
 * there is no CSS at all.
 *
 * **Why this file exists.** `card-map.ts` has two consumers: the browser, where
 * `var(--ramp-4)` resolves against `tokens.css` and follows the visitor's
 * theme, and an OpenGraph Lambda (plan §23) that rasterizes the same SVG with
 * resvg. A rasterizer has no stylesheet, so every `var(--…)` in the document
 * resolves to nothing — the card would come out black-on-black or fully
 * transparent, and *the browser would keep working the whole time*. That is a
 * silent failure with no local symptom, which is why the renderer refuses to
 * name a colour at all and takes a resolved `MapPalette` instead.
 *
 * **Why the light-mode hexes are duplicated here, and why that is safe.**
 * CLAUDE.md's design-system rule is that `tokens.css` is the only place a
 * literal colour appears, and this file breaks that rule on purpose because CSS
 * cannot be imported by a Lambda bundle without dragging in an environment
 * (Vite's `?raw`, an esbuild loader) — the exact coupling the shared module is
 * supposed to be free of. So `LIGHT_TOKENS` is a **mirror, not a second
 * source**: `site/test/card-map.test.ts` parses the bare `:root` block of
 * `site/src/styles/tokens.css` on every test run and fails if any value here
 * has drifted by so much as a digit. Both palettes are then built by
 * `paletteFrom` over one token-name list, so the browser palette and the card
 * palette cannot reference different sets of tokens either.
 *
 * **Fonts are palette, not decoration.** They have the identical failure mode:
 * `var(--font-mono)` means nothing to a rasterizer, and none of the named faces
 * in `--font-mono` (`SF Mono`, `Menlo`, `Cascadia Code`) exist on a Lambda. The
 * stacks in `tokens.css` end in the generic families `sans-serif` and
 * `monospace`, which is what makes `LIGHT_PALETTE` safe *in a browser* — keep
 * that property if they change. It is **not** enough for a rasterizer: usvg
 * does not resolve generic family keywords at all, so a card consumer must
 * override these two fields with the concrete faces it ships. `og/` does
 * exactly that, and `MapPalette` carries type precisely so it can.
 */

/**
 * The light-mode values of every token the map renders with.
 *
 * Mirrored from the bare `:root` block of `site/src/styles/tokens.css`, and
 * verified against it by test. Light mode specifically: a share card is a
 * standalone image with no viewer preference to consult, the same reason
 * `site/public/favicon.svg` carries its own hexes.
 */
export const LIGHT_TOKENS = {
  ink: "#12141a",
  "ink-2": "#4a5160",
  muted: "#656d7c",
  surface: "#ffffff",
  plane: "#f7f8fa",
  border: "#dfe3ea",
  accent: "#4b3fd0",

  "series-1": "#2a78d6",
  "series-2": "#eb6834",
  "series-3": "#1baf7a",
  "series-4": "#eda100",
  "series-5": "#e87ba4",
  "series-6": "#008300",
  "series-7": "#4a3aa7",
  "series-8": "#e34948",

  "ramp-1": "#cde2fb",
  "ramp-2": "#b7d3f6",
  "ramp-3": "#9ec5f4",
  "ramp-4": "#86b6ef",
  "ramp-5": "#6da7ec",
  "ramp-6": "#5598e7",
  "ramp-7": "#3987e5",
  "ramp-8": "#2a78d6",
  "ramp-9": "#1c5cab",

  "ramp-ink-1": "#000000",
  "ramp-ink-2": "#000000",
  "ramp-ink-3": "#000000",
  "ramp-ink-4": "#000000",
  "ramp-ink-5": "#000000",
  "ramp-ink-6": "#000000",
  "ramp-ink-7": "#000000",
  "ramp-ink-8": "#000000",
  "ramp-ink-9": "#ffffff",

  "series-ink-1": "#000000",
  "series-ink-2": "#000000",
  "series-ink-3": "#000000",
  "series-ink-4": "#000000",
  "series-ink-5": "#000000",
  "series-ink-6": "#ffffff",
  "series-ink-7": "#ffffff",
  "series-ink-8": "#000000",

  "font-sans": 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "font-mono": 'ui-monospace, "SF Mono", Menlo, "Cascadia Code", Consolas, monospace',
  "diverge-1": "#1c5cab",
  "diverge-2": "#5381bf",
  "diverge-3": "#85a5d1",
  "diverge-4": "#b8c9e2",
  "diverge-5": "#eceef2",
  "diverge-6": "#dec6bc",
  "diverge-7": "#ce9f87",
  "diverge-8": "#bc7853",
  "diverge-9": "#a8500f",
  "diverge-ink-1": "#ffffff",
  "diverge-ink-2": "#000000",
  "diverge-ink-3": "#000000",
  "diverge-ink-4": "#000000",
  "diverge-ink-5": "#000000",
  "diverge-ink-6": "#000000",
  "diverge-ink-7": "#000000",
  "diverge-ink-8": "#000000",
  "diverge-ink-9": "#ffffff",
} as const;

/** Every token the renderer is allowed to ask for. */
export type MapTokenName = keyof typeof LIGHT_TOKENS;

/**
 * Resolved paint and type for one render.
 *
 * Every field is a literal the output can carry verbatim — a hex in the card's
 * case, a `var(--…)` reference in the browser's. The renderer never inspects
 * these values, so it cannot accidentally grow a dependency on either form.
 */
export interface MapPalette {
  /** Sequential ramp, low→high. Nine steps, matching `--ramp-1…9`. */
  readonly ramp: readonly string[];
  /**
   * The ramp as a **continuous** function: a fill for any fraction in [0, 1].
   *
   * Nine discrete steps were quantising away differences the map exists to
   * show — on a GGUF card, `Q4_K` at 4.50 and `Q5_K` at 5.50 landed on the same
   * step, a 22% cost difference in one colour, while five of the nine steps
   * painted a range nothing occupied. Resolution was never the fix: the same
   * compression just arrives more finely. Continuity is (owner, 2026-08-10).
   *
   * Each palette answers in the form its surface can re-theme:
   *
   * - the browser gets `color-mix(in oklab, …)`, so the fill is still CSS and
   *   still swaps with `data-theme` **without a re-render** — the theme toggle
   *   deliberately repaints nothing but its own button, and a literal colour
   *   here would leave a dark-mode page holding light-mode cells;
   * - the card gets an interpolated literal, because a rasterizer has no
   *   stylesheet and `color-mix` would render as nothing.
   *
   * OKLab rather than HSV or sRGB: the interpolation has to be perceptually
   * even for equal fractions to read as equal magnitude steps, which is the
   * whole point, and it is the space this project already validates palettes in.
   */
  readonly rampAt: (fraction: number) => string;
  /**
   * Legible ink for a label printed on `rampAt(fraction)`.
   *
   * Still the design system's call rather than the renderer's, for the reason
   * `rampInk` gives — in the browser the fill is a `color-mix` of two custom
   * properties and has no luminance to threshold. What the system supplies now
   * is the *crossover fraction* instead of a per-step answer, which is the same
   * decision expressed continuously.
   */
  readonly rampInkAt: (fraction: number) => string;
  /**
   * Diverging scale for a **signed** quantity, as a continuous function.
   *
   * `0` is the low pole, `0.5` the neutral midpoint, `1` the high pole. Used by
   * the compare map, where the cell encodes B minus A and the sign is the point:
   * a sequential ramp puts "much lower" and "much higher" at opposite ends of
   * one hue with nothing marking which side of zero a cell is on.
   *
   * Same two forms as `rampAt` and for the same reasons — `color-mix` in the
   * browser so the theme toggle repaints nothing, an interpolated literal for
   * the rasterizer, both in OKLab so equal fractions read as equal steps.
   */
  /** The nine discrete steps, for a legend that needs its poles and midpoint. */
  readonly diverge: readonly string[];
  readonly divergeAt: (fraction: number) => string;
  /** Legible ink for a label printed on `divergeAt(fraction)`. */
  readonly divergeInkAt: (fraction: number) => string;
  /** Categorical slots for the dtype map, in fixed order. Eight. */
  readonly series: readonly string[];
  /**
   * Legible ink for a label printed *on* `ramp[i]`, index for index.
   *
   * A parallel array rather than something the renderer works out, because the
   * renderer cannot work it out: in the browser it holds `var(--ramp-4)`, which
   * has no luminance to threshold, and the answer inverts with the theme
   * anyway. `tokens.css` decides per step and documents the measurement; this
   * only carries the decision across.
   */
  readonly rampInk: readonly string[];
  /** The same, for a label printed on `series[i]`. */
  readonly seriesInk: readonly string[];
  /** Body ink — headlines on the card. */
  readonly ink: string;
  /** Secondary ink — row labels. */
  readonly inkMuted: string;
  /** Metadata ink — axis ticks, legend notes, the card footer. */
  readonly faint: string;
  /** The ground the map is drawn on. */
  readonly surface: string;
  readonly border: string;
  /** Brand accent — the card's logo watermark. */
  readonly accent: string;
  readonly fontSans: string;
  readonly fontMono: string;
}

const RAMP_TOKENS = [
  "ramp-1",
  "ramp-2",
  "ramp-3",
  "ramp-4",
  "ramp-5",
  "ramp-6",
  "ramp-7",
  "ramp-8",
  "ramp-9",
] as const satisfies readonly MapTokenName[];

const SERIES_TOKENS = [
  "series-1",
  "series-2",
  "series-3",
  "series-4",
  "series-5",
  "series-6",
  "series-7",
  "series-8",
] as const satisfies readonly MapTokenName[];

const DIVERGE_TOKENS = [
  "diverge-1",
  "diverge-2",
  "diverge-3",
  "diverge-4",
  "diverge-5",
  "diverge-6",
  "diverge-7",
  "diverge-8",
  "diverge-9",
] as const satisfies readonly MapTokenName[];

const DIVERGE_INK_TOKENS = [
  "diverge-ink-1",
  "diverge-ink-2",
  "diverge-ink-3",
  "diverge-ink-4",
  "diverge-ink-5",
  "diverge-ink-6",
  "diverge-ink-7",
  "diverge-ink-8",
  "diverge-ink-9",
] as const satisfies readonly MapTokenName[];

const RAMP_INK_TOKENS = [
  "ramp-ink-1",
  "ramp-ink-2",
  "ramp-ink-3",
  "ramp-ink-4",
  "ramp-ink-5",
  "ramp-ink-6",
  "ramp-ink-7",
  "ramp-ink-8",
  "ramp-ink-9",
] as const satisfies readonly MapTokenName[];

const SERIES_INK_TOKENS = [
  "series-ink-1",
  "series-ink-2",
  "series-ink-3",
  "series-ink-4",
  "series-ink-5",
  "series-ink-6",
  "series-ink-7",
  "series-ink-8",
] as const satisfies readonly MapTokenName[];

/**
 * Build a palette by resolving each token name one way.
 *
 * The single place the token *names* are written down, so the two palettes
 * below are structurally identical by construction. Adding a token to the
 * renderer means adding it here once, and `MapTokenName` then forces a
 * light-mode value to exist for it.
 */

/**
 * Which discrete ink token covers a continuous fraction.
 *
 * **The fill is continuous; the ink is not, and must not be.** A first attempt
 * derived one crossover fraction and picked black below it and white above.
 * That is wrong in a way only dark mode shows: light flips at `--ramp-ink-9`
 * alone (8/9 of the way up), while dark flips at `--ramp-ink-5` *and inverts* —
 * white at the bottom, black above. One threshold cannot describe both, and
 * `CSS_VAR_PALETTE` serves both from one render, because the browser resolves
 * `var(--ramp-ink-4)` differently under `data-theme`. The first version put
 * white ink on mid-ramp fills in dark mode: a contrast failure, not a style
 * difference (owner spotted it, 2026-08-10).
 *
 * So the renderer keeps emitting a *token index* and the design system keeps
 * answering per step, per theme, exactly as `tokens.css` documents. Only the
 * fill gained resolution; the decision about legible ink never needed any.
 *
 * Matches the old `rampStep` mapping one for one, so no cell's label changes.
 */
function inkIndexFor(fraction: number, steps: number): number {
  const step = Math.min(steps, Math.max(1, Math.ceil(unit(fraction) * steps) || 1));
  return step - 1;
}

/** Clamp into the unit interval; a fraction outside it is a caller's bug, not a colour. */
function unit(fraction: number): number {
  return Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
}

/* -- OKLab, for the card's numeric interpolation --------------------------- */

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

function hexToOklab(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = srgbToLinear(((n >> 16) & 255) / 255);
  const g = srgbToLinear(((n >> 8) & 255) / 255);
  const b = srgbToLinear((n & 255) / 255);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklabToHex([L, A, B]: [number, number, number]): string {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  const to = (v: number) =>
    Math.round(Math.min(255, Math.max(0, linearToSrgb(v) * 255)))
      .toString(16)
      .padStart(2, "0");
  return `#${to(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s)}${to(
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
  )}${to(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)}`;
}

/**
 * The ramp as a continuous function, over the *discrete* tokens.
 *
 * Interpolating between the nine rather than between only the endpoints: the
 * steps were validated as a set, so their intermediate hues are the checked
 * path from light to dark, and a straight endpoint-to-endpoint line would leave
 * it. Continuity is the goal; a different ramp is not.
 */
function interpolateRamp(ramp: readonly string[], fraction: number): string {
  const f = unit(fraction);
  const scaled = f * (ramp.length - 1);
  const i = Math.min(ramp.length - 2, Math.floor(scaled));
  const t = scaled - i;
  const a = hexToOklab(ramp[i] as string);
  const b = hexToOklab(ramp[i + 1] as string);
  return oklabToHex([
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ]);
}

function paletteFrom(
  resolve: (token: MapTokenName) => string,
  continuous?: {
    readonly rampAt: (ramp: readonly string[], fraction: number) => string;
  },
): MapPalette {
  const ramp = RAMP_TOKENS.map(resolve);
  const rampInk = RAMP_INK_TOKENS.map(resolve);
  const diverge = DIVERGE_TOKENS.map(resolve);
  const divergeInk = DIVERGE_INK_TOKENS.map(resolve);
  return {
    ramp,
    rampInk,
    rampAt: (fraction) =>
      continuous
        ? continuous.rampAt(ramp, fraction)
        : // No continuous form supplied: fall back to the nearest step, so a
          // palette built by a consumer we do not know about still renders.
          (ramp[Math.min(ramp.length - 1, Math.floor(unit(fraction) * ramp.length))] ??
          (ramp[0] as string)),
    rampInkAt: (fraction) => rampInk[inkIndexFor(fraction, rampInk.length)] as string,
    diverge,
    // The same interpolation the sequential ramp uses — a nine-step array is a
    // nine-step array, and `interpolateRamp` never knew which hue it held.
    divergeAt: (fraction) =>
      continuous
        ? continuous.rampAt(diverge, fraction)
        : (diverge[
            Math.min(diverge.length - 1, Math.floor(unit(fraction) * diverge.length))
          ] ?? (diverge[0] as string)),
    divergeInkAt: (fraction) =>
      divergeInk[inkIndexFor(fraction, divergeInk.length)] as string,
    series: SERIES_TOKENS.map(resolve),
    seriesInk: SERIES_INK_TOKENS.map(resolve),
    ink: resolve("ink"),
    inkMuted: resolve("ink-2"),
    faint: resolve("muted"),
    surface: resolve("surface"),
    border: resolve("border"),
    accent: resolve("accent"),
    fontSans: resolve("font-sans"),
    fontMono: resolve("font-mono"),
  };
}

/**
 * The browser palette: custom-property references, so the map re-themes with
 * the page — including the deliberately *inverted* dark-mode ramp — without
 * re-rendering. This is what keeps three-state theming working after the
 * extraction.
 */
/**
 * The dark-mode values of the same tokens, mirrored from the
 * `prefers-color-scheme: dark` block of `site/src/styles/tokens.css`.
 *
 * Generated from that file rather than transcribed, and asserted against it by
 * the same test that guards `LIGHT_TOKENS` — forty values copied by hand is
 * forty chances to introduce a colour that exists nowhere else.
 *
 * Note the ramp **inverts**: `ramp-1` is the lightest step in light mode and the
 * darkest in dark mode, and `ramp-ink-*` inverts with it. A card palette that
 * mixed the two would print black labels on near-black cells.
 */
export const DARK_TOKENS = {
  ink: "#f2f4f8",
  "ink-2": "#b3bccb",
  muted: "#868f9f",
  surface: "#161a21",
  plane: "#0d0f14",
  border: "#272d38",
  accent: "#8f86f0",

  "series-1": "#3987e5",
  "series-2": "#d95926",
  "series-3": "#199e70",
  "series-4": "#c98500",
  "series-5": "#d55181",
  "series-6": "#008300",
  "series-7": "#9085e9",
  "series-8": "#e66767",

  "ramp-1": "#16304f",
  "ramp-2": "#1c4272",
  "ramp-3": "#22548f",
  "ramp-4": "#2a68ac",
  "ramp-5": "#3078c9",
  "ramp-6": "#3987e5",
  "ramp-7": "#6da7ec",
  "ramp-8": "#9ec5f4",
  "ramp-9": "#cde2fb",

  "ramp-ink-1": "#ffffff",
  "ramp-ink-2": "#ffffff",
  "ramp-ink-3": "#ffffff",
  "ramp-ink-4": "#ffffff",
  "ramp-ink-5": "#000000",
  "ramp-ink-6": "#000000",
  "ramp-ink-7": "#000000",
  "ramp-ink-8": "#000000",
  "ramp-ink-9": "#000000",

  "series-ink-1": "#000000",
  "series-ink-2": "#000000",
  "series-ink-3": "#000000",
  "series-ink-4": "#000000",
  "series-ink-5": "#000000",
  "series-ink-6": "#ffffff",
  "series-ink-7": "#000000",
  "series-ink-8": "#000000",
  // Type stacks are not a theme concern — `tokens.css` declares them once, in
  // the bare `:root`, and the dark block does not touch them. Taken from
  // `LIGHT_TOKENS` rather than repeated so there is still exactly one copy.
  "font-sans": LIGHT_TOKENS["font-sans"],
  "font-mono": LIGHT_TOKENS["font-mono"],
  "diverge-1": "#8fc0f5",
  "diverge-2": "#7499c3",
  "diverge-3": "#5b7592",
  "diverge-4": "#425265",
  "diverge-5": "#2b313b",
  "diverge-6": "#584d48",
  "diverge-7": "#886953",
  "diverge-8": "#bb865c",
  "diverge-9": "#f0a463",
  "diverge-ink-1": "#000000",
  "diverge-ink-2": "#000000",
  "diverge-ink-3": "#ffffff",
  "diverge-ink-4": "#ffffff",
  "diverge-ink-5": "#ffffff",
  "diverge-ink-6": "#ffffff",
  "diverge-ink-7": "#ffffff",
  "diverge-ink-8": "#000000",
  "diverge-ink-9": "#000000",
} as const satisfies Record<MapTokenName, string>;

export const DARK_PALETTE: MapPalette = paletteFrom((token) => DARK_TOKENS[token]);

export const CSS_VAR_PALETTE: MapPalette = paletteFrom((token) => `var(--${token})`, {
  /*
   * `color-mix` rather than a computed literal, so the fill stays CSS.
   *
   * The theme toggle repaints nothing but its own button — the palette
   * "swaps via CSS on its own" (see `ui/theme.ts`), because every cell holds
   * `var(--ramp-4)` and the browser re-resolves it. A hex here would leave a
   * dark-mode page wearing light-mode cells until something re-rendered,
   * which no test would catch and every visitor would.
   *
   * `in oklab` so the browser interpolates in the same space the card does.
   */
  rampAt: (ramp, fraction) => {
    const f = unit(fraction);
    const scaled = f * (ramp.length - 1);
    const i = Math.min(ramp.length - 2, Math.floor(scaled));
    const percent = ((scaled - i) * 100).toFixed(2);
    return `color-mix(in oklab, ${ramp[i]}, ${ramp[i + 1]} ${percent}%)`;
  },
});

/**
 * The card palette: resolved light-mode literals, for any consumer without a
 * stylesheet. Used by the OpenGraph rasterizer, and by tests as the proof that
 * a `var(--…)` has not crept back into card output.
 */
export const LIGHT_PALETTE: MapPalette = paletteFrom(
  (token) => LIGHT_TOKENS[token],
  // Numeric, because a rasterizer has no stylesheet and would draw nothing for
  // a `color-mix`. Same OKLab path the browser takes, so the two agree.
  { rampAt: interpolateRamp },
);
